import { AlertCircle, Download } from 'lucide-react';

import { LoadingSpinner } from '@/components/LoadingSpinner';
import { Modal } from '@/components/Modal';

import { useExportClaims } from '@/hooks/useExportClaims';

import { IAdminClaim, IExportClaimsParams } from '@/services/admin';

import { claimsToCsv, downloadCsv } from '@/utils/csv';

interface ExportClaimsModalProps {
  selectedClaims: IAdminClaim[];
  mode: 'selected' | 'all';
  filterParams: IExportClaimsParams;
  /** Set when the current filters can never match a claim, such as every group deselected.
   * `filterParams` cannot express that, so skip the request and export zero records. */
  filtersMatchNothing?: boolean;
  onClose: () => void;
}

export function ExportClaimsModal({
  selectedClaims,
  mode,
  filterParams,
  filtersMatchNothing = false,
  onClose,
}: ExportClaimsModalProps) {
  const isExportAll = mode === 'all';
  const { data, loading, error } = useExportClaims(
    filterParams,
    isExportAll && !filtersMatchNothing,
  );

  const claimsToExport = isExportAll ? (data?.items ?? []) : selectedClaims;
  const truncated = isExportAll ? (data?.truncated ?? false) : false;
  const phase: 'loading' | 'confirm' | 'error' =
    isExportAll && loading ? 'loading' : isExportAll && error ? 'error' : 'confirm';

  const handleExport = () => {
    const csv = claimsToCsv(claimsToExport);
    const filename = `claims-export-${new Date().toISOString().slice(0, 10)}.csv`;
    downloadCsv(filename, csv);
    onClose();
  };

  return (
    <Modal title="Export Claims" onClose={onClose} maxWidth="sm" titleSize="lg">
      {phase === 'loading' && <LoadingSpinner message="Loading claims matching your filters..." />}

      {phase === 'error' && (
        <div className="space-y-4">
          <div className="flex items-start gap-4">
            <div className="shrink-0">
              <div className="flex size-12 items-center justify-center rounded-full bg-red-100">
                <AlertCircle className="size-6 text-red-600" />
              </div>
            </div>
            <p className="flex-1 text-gray-600">{error || 'Failed to load claims for export.'}</p>
          </div>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-300 px-4 py-2 font-medium text-gray-700 hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {phase === 'confirm' && (
        <div className="space-y-6">
          <div className="flex items-start gap-4">
            <div className="shrink-0">
              <div className="flex size-12 items-center justify-center rounded-full bg-red-100">
                <Download className="size-6 text-red-600" />
              </div>
            </div>
            <div className="flex-1 space-y-2">
              <p className="text-gray-600">
                Export <span className="font-semibold">{claimsToExport.length}</span> record
                {claimsToExport.length === 1 ? '' : 's'}?
              </p>
              {truncated && (
                <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
                  There are more matching claims than can be exported in one file. This export is
                  limited to the first {claimsToExport.length} records.
                </p>
              )}
            </div>
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-md border border-gray-300 px-4 py-2 font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleExport}
              className="flex-1 rounded-md bg-red-600 px-4 py-2 font-medium text-white hover:bg-red-700"
            >
              Export
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

export default ExportClaimsModal;
